import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, Skeleton } from '@chakra-ui/react';
import { NumberChartCard } from '@/components/NumberChartCard';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useSubscriptionPlans } from '@/services/queries/subscription-plan';
import { useFinancialMonthlyPayment } from '@/services/queries/financial';
import { breadcrumb } from './breadcrumb';
import { FinancialFiltersBox } from './components/FinancialFiltersBox';
import { FinancialTable } from './components/FinancialTable';
import { Filters } from './types';
import { CommercialConditionsModal } from './components/CommercialConditionsModal';
import { useTranslation } from 'react-i18next';

const sortOrder = 'desc';
const sortField = 'created_at';

export const Financial = () => {
  const { t } = useTranslation();
  const [commercialConditionsModalOpen, setCommercialConditionsModalOpen] = useState(false);
  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const filtersForm = useFiltersForm<Filters>({
    defaultValues: {
      name: '',
      transaction_type: '',
      subscription_plan: '',
      dateValue: undefined,
      end_date: '',
      start_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const params = GetParamsObject(filterData, pagination.pageNumber, pagination.pageSize, sortOrder, sortField);

  const { data: itemData, isLoading } = useFinancialMonthlyPayment(params);
  const { data: subscriptionPlansData } = useSubscriptionPlans(GetParamsObject({}, 1, 900, 'asc', 'created_at'));

  const itemsData = itemData?.data?.financials ?? [];
  const subscriptionPlans = subscriptionPlansData?.data ?? [];

  return (
    <PageContent
      title={t('pages.financial.financial_title')}
      breadcrumbItems={breadcrumb}
      actionsContent={
        <Button
          display='flex'
          alignItems='center'
          gap={2}
          onClick={() => setCommercialConditionsModalOpen(true)}
          width={{ base: '100%', md: 'initial' }}
        >
          <ChakraFeatherIcon name='file-text' />
          {t('pages.financial.financial_view_commercial_conditions')}
        </Button>
      }
    >
      <Flex direction='column' gap='small'>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <FinancialFiltersBox filtersForm={filtersForm} subscriptionPlans={subscriptionPlans} />
        </SectionBox>

        {isLoading ? (
          <Skeleton />
        ) : (
          <NumberChartCard
            width={{ md: 'fit-content' }}
            label={t('pages.financial.financial_total_revenue')}
            value={new Intl.NumberFormat('pt-BR', {
              style: 'currency',
              currency: 'BRL',
            }).format(itemData?.data?.chart?.total ?? 0)}
          />
        )}

        <SectionBox bodyProps={{ gap: 'small' }}>
          <Flex direction='column' gap='deka'>
            <Flex direction='column' gap='micro'>
              <FinancialTable
                isLoading={isLoading}
                data={itemsData}
                sorting={sorting}
                pageNumber={pagination.pageNumber}
                pageSize={pagination.pageSize}
                totalPages={itemData?.meta.pages ?? 0}
                totalItems={itemData?.meta.total ?? 0}
                onSortingChange={(field, desc) => setSorting({ field, desc })}
                onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
                onViewClick={item => navigate(`/financial/${item.id}`)}
              />
            </Flex>
          </Flex>
        </SectionBox>
      </Flex>

      <CommercialConditionsModal
        isOpen={commercialConditionsModalOpen}
        onClose={() => setCommercialConditionsModalOpen(false)}
        onConfirm={() => {
          console.log('Exportar');
          setCommercialConditionsModalOpen(false);
        }}
      />
    </PageContent>
  );
};